// Expert profiles management in Settings
import { useState } from 'react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Badge } from '@/components/ui/badge'
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Users, Plus, Pencil, Trash2, CheckCircle2, Loader2, X } from 'lucide-react'
import { useExpertProfiles, type ExpertProfile } from '@/hooks/useExpertProfiles'
import { EXPERTISE_DOMAINS, D365_MODULES, D365_VERSIONS } from '@/lib/constants'

interface FormState {
  full_name: string
  email: string
  expertise_domains: string[]
  d365_modules: string[]
  d365_versions: string[]
  max_concurrent_assignments: number
  availability_status: string
  is_validator: boolean
}

const EMPTY_FORM: FormState = {
  full_name: '',
  email: '',
  expertise_domains: [],
  d365_modules: [],
  d365_versions: [],
  max_concurrent_assignments: 5,
  availability_status: 'available',
  is_validator: false,
}

export function ExpertsSettingsTab(): JSX.Element {
  const { profiles, isLoading, createProfile, updateProfile, deleteProfile, isSaving } = useExpertProfiles()
  const [editing, setEditing] = useState<string | null>(null)
  const [showForm, setShowForm] = useState(false)
  const [form, setForm] = useState<FormState>(EMPTY_FORM)
  const [saveSuccess, setSaveSuccess] = useState(false)

  const handleEdit = (profile: ExpertProfile) => {
    setEditing(profile.id)
    setForm({
      full_name: profile.full_name,
      email: profile.email,
      expertise_domains: profile.expertise_domains,
      d365_modules: profile.d365_modules,
      d365_versions: profile.d365_versions,
      max_concurrent_assignments: profile.max_concurrent_assignments,
      availability_status: profile.availability_status,
      is_validator: profile.is_validator,
    })
    setShowForm(true)
  }

  const handleNew = () => {
    setEditing(null)
    setForm(EMPTY_FORM)
    setShowForm(true)
  }

  const handleSave = async () => {
    try {
      if (editing) {
        await updateProfile({ id: editing, ...form })
      } else {
        await createProfile(form)
      }
      setShowForm(false)
      setEditing(null)
      setForm(EMPTY_FORM)
      setSaveSuccess(true)
      setTimeout(() => setSaveSuccess(false), 3000)
    } catch (err) {
      console.error('Save failed:', err)
    }
  }

  const handleDelete = async (id: string) => {
    try {
      await deleteProfile(id)
    } catch (err) {
      console.error('Delete failed:', err)
    }
  }

  const toggleArrayField = (field: 'expertise_domains' | 'd365_modules' | 'd365_versions', value: string) => {
    setForm((prev) => ({
      ...prev,
      [field]: prev[field].includes(value)
        ? prev[field].filter((v) => v !== value)
        : [...prev[field], value],
    }))
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Users className="h-5 w-5" />
          Expert Profiles
        </CardTitle>
        <CardDescription>
          Manage expert profiles for issue assignment and solution validation
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {saveSuccess && (
          <Alert className="border-kaycoo-teal bg-kaycoo-teal-pale">
            <CheckCircle2 className="h-4 w-4 text-kaycoo-teal-dark" />
            <AlertDescription className="text-kaycoo-teal-dark">Expert profile saved.</AlertDescription>
          </Alert>
        )}

        <div className="flex justify-end">
          <Button onClick={handleNew} size="sm" className="bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2">
            <Plus className="mr-1.5 h-4 w-4" />
            Add Expert
          </Button>
        </div>

        {/* Expert list */}
        {isLoading ? (
          <div className="py-4 text-center text-sm text-muted-foreground">Loading experts...</div>
        ) : profiles.length === 0 ? (
          <div className="py-4 text-center text-sm text-muted-foreground">No expert profiles yet.</div>
        ) : (
          <div className="rounded-lg border">
            <div className="grid grid-cols-12 gap-2 border-b bg-muted/50 px-4 py-2 text-xs font-medium text-muted-foreground">
              <div className="col-span-3">Name</div>
              <div className="col-span-3">Email</div>
              <div className="col-span-3">Domains</div>
              <div className="col-span-1">Status</div>
              <div className="col-span-2 text-right">Actions</div>
            </div>
            {profiles.map((p) => (
              <div key={p.id} className="grid grid-cols-12 gap-2 border-b px-4 py-3 text-sm last:border-b-0 hover:bg-muted/30">
                <div className="col-span-3 flex items-center gap-1.5">
                  <span className="truncate font-medium">{p.full_name}</span>
                  {p.is_validator && <Badge variant="outline" className="text-xs">Validator</Badge>}
                </div>
                <div className="col-span-3 flex items-center text-muted-foreground truncate">{p.email}</div>
                <div className="col-span-3 flex items-center flex-wrap gap-1">
                  {p.expertise_domains.slice(0, 2).map((d) => (
                    <Badge key={d} variant="secondary" className="text-xs">{d}</Badge>
                  ))}
                  {p.expertise_domains.length > 2 && (
                    <Badge variant="secondary" className="text-xs">+{p.expertise_domains.length - 2}</Badge>
                  )}
                </div>
                <div className="col-span-1 flex items-center">
                  <Badge variant={p.availability_status === 'available' ? 'default' : 'secondary'} className="text-xs capitalize">
                    {p.availability_status}
                  </Badge>
                </div>
                <div className="col-span-2 flex items-center justify-end gap-1">
                  <Button variant="ghost" size="sm" onClick={() => handleEdit(p)}>
                    <Pencil className="h-3.5 w-3.5" />
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => handleDelete(p.id)}>
                    <Trash2 className="h-3.5 w-3.5 text-kaycoo-burgundy" />
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Add/Edit form */}
        {showForm && (
          <div className="space-y-4 rounded-lg border border-kaycoo-teal-light bg-kaycoo-teal-pale/30 p-4">
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-medium">{editing ? 'Edit Expert' : 'New Expert'}</h4>
              <Button variant="ghost" size="sm" onClick={() => { setShowForm(false); setEditing(null) }}>
                <X className="h-4 w-4" />
              </Button>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <div className="space-y-1.5">
                <Label>Full Name</Label>
                <Input value={form.full_name} onChange={(e) => setForm((p) => ({ ...p, full_name: e.target.value }))} />
              </div>
              <div className="space-y-1.5">
                <Label>Email</Label>
                <Input type="email" value={form.email} onChange={(e) => setForm((p) => ({ ...p, email: e.target.value }))} />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Expertise Domains</Label>
              <div className="flex flex-wrap gap-1.5">
                {EXPERTISE_DOMAINS.map((d) => (
                  <Button
                    key={d.value}
                    size="sm"
                    variant={form.expertise_domains.includes(d.value) ? 'default' : 'outline'}
                    className={form.expertise_domains.includes(d.value) ? 'bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2' : ''}
                    onClick={() => toggleArrayField('expertise_domains', d.value)}
                  >
                    {d.label}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>D365 Modules</Label>
              <div className="flex flex-wrap gap-1.5">
                {D365_MODULES.map((m) => (
                  <Button
                    key={m}
                    size="sm"
                    variant={form.d365_modules.includes(m) ? 'default' : 'outline'}
                    className={form.d365_modules.includes(m) ? 'bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2' : ''}
                    onClick={() => toggleArrayField('d365_modules', m)}
                  >
                    {m}
                  </Button>
                ))}
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>D365 Versions</Label>
              <div className="flex flex-wrap gap-1.5">
                {D365_VERSIONS.map((v) => (
                  <Button
                    key={v}
                    size="sm"
                    variant={form.d365_versions.includes(v) ? 'default' : 'outline'}
                    className={form.d365_versions.includes(v) ? 'bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2' : ''}
                    onClick={() => toggleArrayField('d365_versions', v)}
                  >
                    {v}
                  </Button>
                ))}
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-1.5">
                <Label>Max Concurrent</Label>
                <Input
                  type="number"
                  min={1}
                  max={20}
                  value={form.max_concurrent_assignments}
                  onChange={(e) => setForm((p) => ({ ...p, max_concurrent_assignments: Number(e.target.value) }))}
                />
              </div>
              <div className="space-y-1.5">
                <Label>Availability</Label>
                <Select value={form.availability_status} onValueChange={(v) => setForm((p) => ({ ...p, availability_status: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="available">Available</SelectItem>
                    <SelectItem value="busy">Busy</SelectItem>
                    <SelectItem value="offline">Offline</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-end gap-2 pb-1">
                <input
                  type="checkbox"
                  id="is-validator"
                  checked={form.is_validator}
                  onChange={(e) => setForm((p) => ({ ...p, is_validator: e.target.checked }))}
                  className="rounded"
                />
                <Label htmlFor="is-validator">Can validate solutions</Label>
              </div>
            </div>

            <Button
              onClick={handleSave}
              disabled={!form.full_name || !form.email || isSaving}
              className="w-full bg-kaycoo-teal-dark text-white hover:bg-kaycoo-teal-dark-2"
            >
              {isSaving ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />}
              {editing ? 'Update Expert' : 'Create Expert'}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  )
}
